import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { MdModeComment } from "react-icons/md";
import { AiFillFlag } from "react-icons/ai";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import ContentTypeMismatchIcon from "components/partials/icon/content-type-mismatch-icon";
import { Chip } from "ui/chip";

import { TouchpointStatusDictionaryItem } from "hooks/api/dictionary";

import {
  Route as AppRoute,
  buildPath,
  buildTouchpointPreviewHref,
  getTouchpointPaths,
} from "utilities/app-routes";
import { toSpacedTitleCase, formatFiscalYear, getDisplayTouchpointIcon } from "utilities";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { formatDateTime } from "utilities/date";

import { TouchpointType } from "types/touchpoint";

import { TouchpointVersionHistoryDto } from "generated/schemas";

interface TableActionsProps {
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[];
}

const headerStyles = "text-gray-600 font-bold leading-5";

/**
 * Column `size`s keep every header on one line: on top of the label's own width a column has to cover
 * the cell's 32px of horizontal padding, the 4px gap and — where the column sorts — the 24px arrow.
 */
export const createInboxTableColumns = ({
  touchpointVersionStatuses,
}: TableActionsProps): ColumnDef<TouchpointVersionHistoryDto>[] => [
  {
    id: "warningIcon",
    enableSorting: false,
    cell: ({ row }) => {
      const isTemplate = row.original.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME;

      return (
        !isTemplate &&
        !row.original.validContentType && (
          <div className="mx-auto size-5">
            <ContentTypeMismatchIcon customClass="w-full" />
          </div>
        )
      );
    },
    size: 60,
    meta: {
      className: "pl-4",
      isFixedWidth: true,
    },
  },
  {
    id: "touchpointName",
    header: "Creative name",
    accessorKey: "touchpointName",
    enableSorting: true,
    cell: ({ row }) => {
      const touchpointType = row.original.touchpointType as TouchpointType;

      return (
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{getDisplayTouchpointIcon(touchpointType)}</div>
          <div className="flex flex-col pr-3">
            <Link
              to={buildTouchpointPreviewHref(
                getTouchpointPaths({
                  clientId: row.original.clientId ?? "",
                  campaignId: row.original.campaignId ?? "",
                  touchpointId: row.original.touchpointId ?? "",
                }).touchpointPreviewPath,
                row.original.touchpointVersionId,
              )}
              state={{
                fromSection: AppRoute.recentCreatives,
                breadcrumbs: [
                  {
                    label: "Recent creatives",
                    href: buildPath(
                      AppRoute.clients,
                      row.original.clientId!,
                      AppRoute.recentCreatives,
                    ),
                  },
                ],
              }}
              className="text-aqua-600 hover:text-aqua-700 font-semibold"
              aria-disabled={false}
              tabIndex={undefined}>
              {row.original.touchpointName}
            </Link>
            <div className="flex gap-3">
              <span>v{row.original.touchpointVersionVersion}</span>
              <Link
                to={`${AppRoute.clients}/${row.original.clientId}/${AppRoute.campaigns}/${row.original.campaignId}/${AppRoute.creative}`}
                className="text-aqua-600 hover:text-aqua-700"
                aria-disabled={false}
                tabIndex={undefined}>
                {row.original.campaignName}
              </Link>
            </div>
          </div>
        </div>
      );
    },
    minSize: 380,
    meta: {
      isFixedWidth: true,
      headerClassName: headerStyles,
      className: "pl-1",
    },
  },
  {
    id: "touchpointVersionFiscalYear",
    header: "Fiscal year",
    accessorKey: "touchpointVersionFiscalYear",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="text-gray-600">
        {formatFiscalYear(Number(row.original.touchpointVersionFiscalYear) || null)}
      </div>
    ),
    size: 150,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "touchpointCopyCode",
    header: "Copy code",
    accessorKey: "touchpointCopyCode",
    enableSorting: true,
    cell: ({ row }) => <div className="text-gray-600">{row.original.touchpointCopyCode}</div>,
    size: 150,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "populationType",
    header: "Population type",
    accessorKey: "populationType",
    enableSorting: true,
    cell: ({ row }) =>
      row.original.populationType ? (
        <Chip
          size="small"
          variant="outlined"
          label={toSpacedTitleCase(row.original.populationType)}
        />
      ) : null,
    size: 200,
    meta: {
      isFixedWidth: true,
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "classes",
    header: "Class",
    accessorKey: "classes",
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original.classes?.length) {
        return null;
      }

      return (
        <div className="flex flex-wrap gap-1">
          {row.original.classes.map((classType) => (
            <Chip
              key={classType}
              size="small"
              variant="outlined"
              color={getTouchpointClassChipColor(classType)}
              label={toSpacedTitleCase(classType)}
            />
          ))}
        </div>
      );
    },
    size: 120,
    meta: {
      isFixedWidth: true,
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "lastModifiedDate",
    header: "Last updated",
    accessorKey: "lastModifiedDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
    ),
    size: 165,
    meta: {
      isFixedWidth: true,
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "touchpointVersionStatus",
    header: "Client review status",
    accessorKey: "touchpointVersionStatus",
    enableSorting: true,
    cell: ({ row }) => {
      const statuses = touchpointVersionStatuses.find(
        (status) => status.status === row.original.touchpointVersionStatus,
      )!;

      return <StatusIndicator versionStatuses={statuses} />;
    },
    size: 215,
    meta: {
      isFixedWidth: true,
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "publishState",
    header: "Publish state",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.touchpointVersionStatus === "PUBLISHED" && (
        <Chip variant="filled" color="success" size="small" label="Published" />
      ),
    size: 150,
    meta: {
      isFixedWidth: true,
      headerClassName: `${headerStyles} pl-4`,
    },
  },
  {
    id: "openComments",
    header: "Open comments",
    accessorKey: "openCommentCount",
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          {!!row.original.openCommentCount && (
            <div className="flex items-center text-teal-700 mr-3">
              <MdModeComment size={15} />
              <span className="ml-1">{row.original.openCommentCount}</span>
            </div>
          )}
          {!!row.original.openActionCommentCount && (
            <div className="flex items-center text-critical-700">
              <AiFillFlag size={15} />
              <span className="ml-1">{row.original.openActionCommentCount}</span>
            </div>
          )}
        </div>
      );
    },
    size: 165,
    meta: {
      isFixedWidth: true,
      headerClassName: headerStyles,
      isHeaderCentered: true,
    },
  },
];
